import { useNavigate, useParams } from "react-router-dom";
import { Bread } from "@/components/Bread";
import { DayLoader } from "@/components/DayLoader";
import { MasteryControl } from "@/components/MasteryControl";
import { POS } from "@/components/POS";
import { Pronounce } from "@/components/Pronounce";
import { useMastery } from "@/hooks/useMastery";
import { highlightWord, pad, pad3 } from "@/lib/format";
import { mark } from "@/lib/study";
import type { DayData, Word } from "@/types/lex";

export function WordDetail() {
  const { day: dayParam, idx: idxParam } = useParams<{ day: string; idx: string }>();
  const day = Number(dayParam) || 1;
  const wordIdx = Number(idxParam) || 1;

  return (
    <DayLoader day={day}>
      {(data) => <Body day={day} wordIdx={wordIdx} data={data} />}
    </DayLoader>
  );
}

interface BodyProps {
  day: number;
  wordIdx: number;
  data: DayData;
}

function Body({ day, wordIdx, data }: BodyProps) {
  const navigate = useNavigate();
  const word = data.words.find((w) => w.index === wordIdx) ?? data.words[0];
  const [mastery] = useMastery(day, word.index);
  const prev = data.words.find((w) => w.index === word.index - 1);
  const next = data.words.find((w) => w.index === word.index + 1);

  return (
    <div className="page word-detail-page">
      <div className="container" style={{ paddingTop: "var(--s-5)" }}>
        <Bread
          crumbs={[
            { label: "Archive", to: "/" },
            { label: `Day ${pad(day)} · ${data.theme_en}`, to: `/day/${day}` },
            { label: `Entry ${pad3(word.index)}` },
          ]}
        />
      </div>

      <section className="container" style={{ paddingTop: "var(--s-6)" }}>
        <div className="word-detail">
          <SidePanel
            word={word}
            total={data.words.length}
            mastery={mastery}
            onMastery={(value) => mark(day, word.index, value)}
            onPrev={prev ? () => navigate(`/word/${day}/${prev.index}`) : undefined}
            onNext={next ? () => navigate(`/word/${day}/${next.index}`) : undefined}
            prevWord={prev}
            nextWord={next}
          />

          <main>
            <WordHeadline word={word} day={day} theme={data.theme_en} />
            <ExampleSection word={word} />
            {word.collocations.length > 0 && <CollocationsSection word={word} />}
            {word.note && <NoteSection note={word.note} />}
          </main>
        </div>
      </section>

      <WordNavBar
        prevWord={prev}
        nextWord={next}
        onPrev={prev ? () => navigate(`/word/${day}/${prev.index}`) : undefined}
        onNext={next ? () => navigate(`/word/${day}/${next.index}`) : undefined}
      />
    </div>
  );
}

/**
 * Fixed prev/next bar shown only on phones (CSS-gated to ≤880px, where the
 * side panel collapses below the article). Keeps navigation a thumb-tap away
 * regardless of how far the reader has scrolled.
 */
function WordNavBar({
  prevWord,
  nextWord,
  onPrev,
  onNext,
}: {
  prevWord?: Word;
  nextWord?: Word;
  onPrev?: () => void;
  onNext?: () => void;
}) {
  return (
    <nav className="word-navbar" aria-label="Word navigation">
      <button className="word-navbar__btn" type="button" onClick={onPrev} disabled={!onPrev}>
        <span className="word-navbar__dir">← Prev</span>
        <span className="word-navbar__word">{prevWord ? prevWord.word : "Start"}</span>
      </button>
      <button
        className="word-navbar__btn word-navbar__btn--next"
        type="button"
        onClick={onNext}
        disabled={!onNext}
      >
        <span className="word-navbar__dir">Next →</span>
        <span className="word-navbar__word">{nextWord ? nextWord.word : "End"}</span>
      </button>
    </nav>
  );
}

interface SidePanelProps {
  word: Word;
  total: number;
  mastery: ReturnType<typeof useMastery>[0];
  onMastery: ReturnType<typeof useMastery>[1];
  onPrev?: () => void;
  onNext?: () => void;
  prevWord?: Word;
  nextWord?: Word;
}

function SidePanel({ word, total, mastery, onMastery, onPrev, onNext, prevWord, nextWord }: SidePanelProps) {
  return (
    <aside className="word-detail__side">
      <div className="t-eyebrow" style={{ marginBottom: 8 }}>Entry</div>
      <div className="t-mono" style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: "var(--s-5)" }}>
        {pad3(word.index)} / {pad3(total)}
      </div>

      <div className="t-eyebrow" style={{ marginBottom: 8 }}>Mastery</div>
      <div style={{ marginBottom: "var(--s-5)" }}>
        <MasteryControl value={mastery} onChange={onMastery} />
      </div>

      <div className="word-detail__navlinks">
      <div className="t-eyebrow" style={{ marginBottom: 8 }}>Navigation</div>
      <div className="stack" style={{ gap: 6 }}>
        {onPrev && prevWord && (
          <a
            onClick={onPrev}
            className="t-mono"
            style={{ cursor: "pointer", fontSize: 12, color: "var(--text-muted)" }}
          >
            ← {pad3(prevWord.index)} · {prevWord.word}
          </a>
        )}
        {onNext && nextWord && (
          <a
            onClick={onNext}
            className="t-mono"
            style={{ cursor: "pointer", fontSize: 12, color: "var(--text-muted)" }}
          >
            {pad3(nextWord.index)} · {nextWord.word} →
          </a>
        )}
      </div>
      </div>
    </aside>
  );
}

function WordHeadline({ word, day, theme }: { word: Word; day: number; theme: string }) {
  return (
    <div className="word-headline">
      <div className="t-mono" style={{ fontSize: 12, color: "var(--text-muted)" }}>
        ENTRY {pad3(word.index)} · DAY {pad(day)} · {theme}
      </div>
      <h1 className="word-headline__word">{word.word}</h1>
      <div className="word-headline__row">
        <Pronounce word={word.word} size={40} />
        <div className="word-headline__ipa">/{word.ipa}/</div>
        <POS value={word.pos} />
        {word.forms.length > 0 && (
          <div className="t-mono" style={{ fontSize: 12, color: "var(--text-faint)" }}>
            forms: {word.forms.join(", ")}
          </div>
        )}
      </div>
      <div className="word-headline__meaning">{word.meaning_vi}</div>
    </div>
  );
}

function ExampleSection({ word }: { word: Word }) {
  const tokens = highlightWord(word.example_en, word.word);
  return (
    <div className="word-block">
      <div className="word-block__label">
        <span>Example · ví dụ</span>
        <span className="word-block__num">§ 01</span>
      </div>
      <p className="word-block__body" style={{ margin: 0 }}>
        {tokens.map((t, i) =>
          t.isMatch ? (
            <em
              key={i}
              style={{ fontStyle: "italic", borderBottom: "1.5px solid var(--ink-0)", paddingBottom: 1 }}
            >
              {t.text}
            </em>
          ) : (
            <span key={i}>{t.text}</span>
          )
        )}
      </p>
      <p className="word-block__body word-block__body--vi" style={{ margin: 0 }}>
        {word.example_vi}
      </p>
    </div>
  );
}

function CollocationsSection({ word }: { word: Word }) {
  return (
    <div className="word-block">
      <div className="word-block__label">
        <span>Collocations · cụm từ</span>
        <span className="word-block__num">§ 02 · {pad(word.collocations.length)}</span>
      </div>
      <div className="colloc-list">
        {word.collocations.map((c, i) => (
          <div key={i} className="colloc-item">
            <div className="colloc-item__num">{pad(i + 1)}</div>
            <div className="colloc-item__en">{c.en}</div>
            <div className="colloc-item__vi">{c.vi}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NoteSection({ note }: { note: string }) {
  return (
    <div className="word-block">
      <div className="word-block__label">
        <span>Editor's note · ghi chú</span>
        <span className="word-block__num">§ 03</span>
      </div>
      <blockquote className="word-block__note">{note}</blockquote>
    </div>
  );
}
